import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Tabs, Tab } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useInventory } from '../../../../../../contexts/InventoryContext';
import UnitOverview from './UnitOverview';
import MediaAndDocuments from '../../../../../../components/inventory/MediaAndDocuments';

const UnitDetailsBase = ({ projectId, configurationId, unitId, unit, project, config, onUnitUpdated }) => {
  const navigate = useNavigate();
  const { updateUnit, inventoryOptions } = useInventory();
  const [basicDetailsEditing, setBasicDetailsEditing] = useState(false);
  const [basicDetailsSaving, setBasicDetailsSaving] = useState(false);
  const [areaEditing, setAreaEditing] = useState(false);
  const [areaSaving, setAreaSaving] = useState(false);
  const [keyHighlightsEditing, setKeyHighlightsEditing] = useState(false);
  const [keyHighlightsSaving, setKeyHighlightsSaving] = useState(false);
  const [otherDetailsEditing, setOtherDetailsEditing] = useState(false);
  const [otherDetailsSaving, setOtherDetailsSaving] = useState(false);
  const [commissionEditing, setCommissionEditing] = useState(false);
  const [commissionSaving, setCommissionSaving] = useState(false);
  const [tagsEditing, setTagsEditing] = useState(false);
  const [tagsSaving, setTagsSaving] = useState(false);
  const [tab, setTab] = useState('overview');

  const handleSaveBasicDetails = async (payload) => {
    if (!unit?.id || !projectId || !configurationId || !updateUnit) return;
    setBasicDetailsSaving(true);
    try {
      const body = {
        name: payload.name ?? unit.name,
        price: payload.price ?? unit.price,
        inventory_status: payload.inventory_status ?? unit.inventory_status,
        floor: payload.floor !== undefined ? payload.floor : unit.floor,
        facing: payload.facing !== undefined ? payload.facing : unit.facing,
        unit_block: payload.unit_block !== undefined ? payload.unit_block : unit.unit_block,
      };
      const res = await updateUnit(projectId, configurationId, unit.id, body);
      if (onUnitUpdated && res) onUnitUpdated(res);
      setBasicDetailsEditing(false);
    } finally {
      setBasicDetailsSaving(false);
    }
  };

  const handleSaveArea = async (payload) => {
    if (!unit?.id || !projectId || !configurationId || !updateUnit) return;
    setAreaSaving(true);
    try {
      const body = { area: payload.area ?? unit.area, area_unit: payload.area_unit ?? unit.area_unit };
      const res = await updateUnit(projectId, configurationId, unit.id, body);
      if (onUnitUpdated && res) onUnitUpdated(res);
      setAreaEditing(false);
    } finally {
      setAreaSaving(false);
    }
  };

  const handleSaveKeyHighlights = async (list) => {
    if (!unit?.id || !projectId || !configurationId || !updateUnit) return;
    setKeyHighlightsSaving(true);
    try {
      const res = await updateUnit(projectId, configurationId, unit.id, { key_highlights: Array.isArray(list) ? list : [] });
      if (onUnitUpdated && res) onUnitUpdated(res);
      setKeyHighlightsEditing(false);
    } finally {
      setKeyHighlightsSaving(false);
    }
  };

  const handleSaveOtherDetails = async (payload) => {
    if (!unit?.id || !projectId || !configurationId || !updateUnit) return;
    setOtherDetailsSaving(true);
    try {
      // Send only key-value pairs from the form; do not merge with unit.metadata (avoids sending amenities: [], facilities: [], etc.).
      const metadata = payload.metadata != null ? { ...payload.metadata } : {};
      const res = await updateUnit(projectId, configurationId, unit.id, { metadata });
      if (onUnitUpdated && res) onUnitUpdated(res);
      setOtherDetailsEditing(false);
    } finally {
      setOtherDetailsSaving(false);
    }
  };

  const handleSaveCommission = async (value) => {
    if (!unit?.id || !projectId || !configurationId || !updateUnit) return;
    setCommissionSaving(true);
    try {
      const num = value != null && value !== '' ? parseFloat(value) : null;
      const res = await updateUnit(projectId, configurationId, unit.id, { commission_rate: num });
      if (onUnitUpdated && res) onUnitUpdated(res);
      setCommissionEditing(false);
    } finally {
      setCommissionSaving(false);
    }
  };

  const handleSaveTags = async (tags) => {
    if (!unit?.id || !projectId || !configurationId || !updateUnit) return;
    setTagsSaving(true);
    try {
      const res = await updateUnit(projectId, configurationId, unit.id, {
        tags: Array.isArray(tags) ? tags : [],
      });
      if (onUnitUpdated && res) onUnitUpdated(res);
      setTagsEditing(false);
    } finally {
      setTagsSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3, width: '100%', alignSelf: 'flex-start' }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`/inventory/projects/${projectId}/configurations/${configurationId}`)}
        sx={{ mb: 2, textTransform: 'none', color: 'success.main' }}
      >
        Back to Units
      </Button>

      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          {unit?.name || unit?.unit_code || 'Unit'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {config?.name ?? '—'} · {project?.name ?? '—'}
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: 2,
          minHeight: 42,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
          '& .Mui-selected': { color: 'success.main' },
          '& .MuiTabs-indicator': { bgcolor: 'success.main' },
        }}
      >
        <Tab label="Overview" value="overview" />
        <Tab label="Media & Documents" value="documents" />
      </Tabs>

      <Box sx={{ width: '100%', alignSelf: 'flex-start' }}>
      {tab === 'overview' && (
      <UnitOverview
        unit={unit}
        project={project}
        config={config}
        areaUnitOptions={inventoryOptions?.area_units ?? []}
        unitStatusOptions={inventoryOptions?.unit_statuses ?? []}
        unitParameterOptions={inventoryOptions?.unit_property_params ?? []}
        facingOptions={inventoryOptions?.facing_directions ?? []}
        isBasicDetailsEditing={basicDetailsEditing}
        onEditBasicDetails={() => setBasicDetailsEditing(true)}
        onSaveBasicDetails={handleSaveBasicDetails}
        onCancelBasicDetails={() => setBasicDetailsEditing(false)}
        basicDetailsSaving={basicDetailsSaving}
        isAreaEditing={areaEditing}
        onEditArea={() => setAreaEditing(true)}
        onSaveArea={handleSaveArea}
        onCancelArea={() => setAreaEditing(false)}
        areaSaving={areaSaving}
        isKeyHighlightsEditing={keyHighlightsEditing}
        onEditKeyHighlights={() => setKeyHighlightsEditing(true)}
        onSaveKeyHighlights={handleSaveKeyHighlights}
        onCancelKeyHighlights={() => setKeyHighlightsEditing(false)}
        keyHighlightsSaving={keyHighlightsSaving}
        isOtherDetailsEditing={otherDetailsEditing}
        onEditOtherDetails={() => setOtherDetailsEditing(true)}
        onSaveOtherDetails={handleSaveOtherDetails}
        onCancelOtherDetails={() => setOtherDetailsEditing(false)}
        otherDetailsSaving={otherDetailsSaving}
        isCommissionEditing={commissionEditing}
        onEditCommission={() => setCommissionEditing(true)}
        onSaveCommission={handleSaveCommission}
        onCancelCommission={() => setCommissionEditing(false)}
        commissionSaving={commissionSaving}
        isTagsEditing={tagsEditing}
        onEditTags={() => setTagsEditing(true)}
        onSaveTags={handleSaveTags}
        onCancelTags={() => setTagsEditing(false)}
        tagsSaving={tagsSaving}
      />
      )}
      {tab === 'documents' && (
        <MediaAndDocuments
          projectId={projectId}
          configurationId={configurationId}
          unitId={unitId}
          config={config}
          unit={unit}
        />
      )}
      </Box>
    </Box>
  );
};

export default UnitDetailsBase;
